/**
 * Common English words that happen to coincide with AR glossary acronyms
 * (`FROM`, `UP`, `HAS`, `WAY`, `END`, …).
 *
 * Why this list exists: TermScanner.findMatches matches case-insensitively
 * by default so lowercase uses of a canonical uppercase acronym — e.g.
 * "the arc team" matching the `ARC` entry — still highlight. That rule is
 * right for most acronyms, but it's wrong when the lowercase form of the
 * acronym is also an English function word: without a guard, every "from"
 * in prose would highlight as the `FROM` acronym. Any term whose
 * `.term.toLowerCase()` is listed below is matched case-sensitively, so
 * only the canonical surface form (`FROM`, not `from`) highlights.
 *
 * Scope: short connector/filler words — articles, prepositions,
 * conjunctions, pronouns, auxiliary verbs, common adverbs, and a handful
 * of frequent 3-letter nouns that double as acronyms (`WAY`, `END`,
 * `TOP`, …). Terms outside this list keep the default case-insensitive
 * behaviour.
 *
 * Trade-off: a glossary entry whose lowercase form is on this list loses
 * the ability to be referenced in lowercase. Authors who want the
 * lowercase form highlighted must write it in uppercase in content —
 * which is consistent with how authors already differentiate the acronym
 * sense from the ordinary word in prose. Add new words here when a false
 * positive is reported; remove a word only after confirming no term
 * depends on the case-sensitive behaviour.
 */
export const COMMON_ENGLISH_STOPWORDS: ReadonlySet<string> = new Set([
    // Articles
    'a',
    'an',
    'the',

    // Prepositions
    'about',
    'above',
    'across',
    'after',
    'against',
    'along',
    'among',
    'around',
    'as',
    'at',
    'before',
    'behind',
    'below',
    'beneath',
    'beside',
    'between',
    'beyond',
    'but',
    'by',
    'despite',
    'down',
    'during',
    'except',
    'for',
    'from',
    'in',
    'inside',
    'into',
    'like',
    'near',
    'of',
    'off',
    'on',
    'onto',
    'out',
    'outside',
    'over',
    'past',
    'since',
    'through',
    'throughout',
    'to',
    'toward',
    'towards',
    'under',
    'underneath',
    'until',
    'up',
    'upon',
    'via',
    'with',
    'within',
    'without',

    // Conjunctions
    'and',
    'or',
    'nor',
    'so',
    'yet',
    'because',
    'if',
    'then',
    'than',
    'while',
    'whereas',
    'though',
    'although',
    'unless',
    'until',
    'when',
    'whenever',
    'where',
    'wherever',
    'how',
    'however',

    // Pronouns & determiners
    'i',
    'me',
    'my',
    'mine',
    'myself',
    'we',
    'us',
    'our',
    'ours',
    'ourselves',
    'you',
    'your',
    'yours',
    'yourself',
    'yourselves',
    'he',
    'him',
    'his',
    'himself',
    'she',
    'her',
    'hers',
    'herself',
    'it',
    'its',
    'itself',
    'they',
    'them',
    'their',
    'theirs',
    'themselves',
    'this',
    'that',
    'these',
    'those',
    'what',
    'which',
    'who',
    'whom',
    'whose',
    'all',
    'any',
    'both',
    'each',
    'either',
    'every',
    'few',
    'many',
    'more',
    'most',
    'much',
    'neither',
    'no',
    'none',
    'other',
    'others',
    'several',
    'some',
    'such',

    // Auxiliary & common verbs
    'am',
    'is',
    'are',
    'was',
    'were',
    'be',
    'been',
    'being',
    'do',
    'does',
    'did',
    'done',
    'doing',
    'have',
    'has',
    'had',
    'having',
    'can',
    'could',
    'will',
    'would',
    'shall',
    'should',
    'may',
    'might',
    'must',
    'ought',
    'go',
    'goes',
    'went',
    'gone',
    'going',
    'get',
    'got',
    'gets',
    'getting',
    'let',
    'lets',
    'letting',
    'see',
    'saw',
    'seen',
    'seeing',
    'say',
    'said',
    'says',
    'saying',
    'know',
    'knew',
    'known',
    'knows',
    'knowing',
    'make',
    'made',
    'makes',
    'making',
    'take',
    'took',
    'taken',
    'takes',
    'taking',
    'use',
    'used',
    'uses',
    'using',

    // Common adverbs
    'again',
    'also',
    'always',
    'back',
    'both',
    'enough',
    'even',
    'ever',
    'far',
    'here',
    'just',
    'later',
    'less',
    'more',
    'much',
    'never',
    'next',
    'not',
    'now',
    'often',
    'once',
    'only',
    'quite',
    'rather',
    'really',
    'soon',
    'still',
    'then',
    'there',
    'together',
    'too',
    'very',
    'well',
    'yes',
    'yet',

    // Short common nouns / adjectives that also appear as acronyms.
    // Added as needed when a false-positive is reported.
    'end',
    'top',
    'way',
    'ways',
    'side',
    'part',
    'parts',
    'day',
    'days',
    'year',
    'years',
    'time',
    'times',
    'item',
    'items',
    'name',
    'names',
    'key',
    'keys',
    'set',
    'sets',
    'map',
    'maps',
    'log',
    'logs',
    'run',
    'runs',
    'red',
    'raw',
    'new',
    'old',
    'low',
    'high',
    'big',
    'small',
    'full',
    'half',
    'last',
    'first',
    'next',
    'only',
    'open',
    'close',
    'kind',
    'form',
    'size',
    'line',
    'lines',
    'point',
    'need',
    'needs',
    'show',
    'make',
    'help',
    'turn',
    'back',
    'home',
    'word',
    'words',
    'work',
    'works',
    'read',
    'write',
    'edit',
    'copy',
    'send',
    'file',
    'files',
    'page',
    'pages',
    'list',
    'lists',
    'rule',
    'rules',
    'test',
    'tests',
    'tool',
    'tools',
    'step',
    'steps',
    'task',
    'tasks',
    'tip',
    'tips',
    'end',
]);

/**
 * Decide whether a glossary term's lowercase form should be allowed as a
 * highlight.
 *
 * Only pure-alpha, all-uppercase, 2+ letter terms qualify for
 * case-sensitive treatment — those are the acronyms whose lowercase form
 * could plausibly collide with ordinary prose. Multi-word phrases,
 * mixed-case terms, and single-letter terms stay case-insensitive, since
 * their surface form is unambiguous (or in the single-letter case, we do
 * not have enough signal to disambiguate).
 *
 * There is no upper length bound: membership in COMMON_ENGLISH_STOPWORDS
 * is the gate. The set already captures long function words (`between`,
 * `through`, `themselves`, `throughout`, …) so a 7+ letter all-caps
 * glossary acronym that happens to collide with one of them is handled
 * correctly instead of being silently ignored.
 */
export function requiresExactCaseMatch(canonicalTerm: string): boolean {
    if (canonicalTerm.length < 2) {
        return false;
    }
    if (!/^[A-Z]+$/.test(canonicalTerm)) {
        return false;
    }
    return COMMON_ENGLISH_STOPWORDS.has(canonicalTerm.toLowerCase());
}
